import { type ReactNode, useRef, useCallback, useMemo } from "react";
import { motion, useMotionValue, useTransform, useSpring, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import { TOP_NAV_OFFSET_CLASS } from "@/lib/layout";

/**
 * Parallax hero background with decorative leaves, sparkles, and floating particles.
 * Layers shift in response to mouse movement for a depth effect.
 * Accepts children to overlay content (login form, hero text, etc.).
 */

// ---------------------------------------------------------------------------
// SVG decorative elements
// ---------------------------------------------------------------------------

function MonsteraLeaf({ style }: { style?: React.CSSProperties }) {
  return (
    <svg viewBox="0 0 200 280" style={style} fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="monstera" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#2d6a3f" />
          <stop offset="50%" stopColor="#1a4a2e" />
          <stop offset="100%" stopColor="#0f3520" />
        </linearGradient>
      </defs>
      <path d="M100 10C60 50 15 100 20 180C25 230 60 260 100 270C140 260 175 230 180 180C185 100 140 50 100 10Z" fill="url(#monstera)" />
      <ellipse cx="65" cy="130" rx="12" ry="20" fill="hsl(213 81% 10%)" opacity="0.9" />
      <ellipse cx="135" cy="125" rx="10" ry="18" fill="hsl(213 81% 10%)" opacity="0.9" />
      <ellipse cx="55" cy="180" rx="8" ry="14" fill="hsl(213 81% 10%)" opacity="0.9" />
      <ellipse cx="145" cy="175" rx="9" ry="15" fill="hsl(213 81% 10%)" opacity="0.9" />
      <path d="M100 30V260" stroke="#3a8a55" strokeWidth="2.5" strokeOpacity="0.6" />
      <path d="M100 70C80 85 55 100 40 120" stroke="#3a8a55" strokeWidth="1.5" strokeOpacity="0.4" />
      <path d="M100 70C120 85 145 100 160 120" stroke="#3a8a55" strokeWidth="1.5" strokeOpacity="0.4" />
      <path d="M100 120C75 140 50 155 35 175" stroke="#3a8a55" strokeWidth="1.5" strokeOpacity="0.4" />
      <path d="M100 120C125 140 150 155 165 175" stroke="#3a8a55" strokeWidth="1.5" strokeOpacity="0.4" />
      <path d="M100 170C80 190 60 205 45 220" stroke="#3a8a55" strokeWidth="1.2" strokeOpacity="0.3" />
      <path d="M100 170C120 190 140 205 155 220" stroke="#3a8a55" strokeWidth="1.2" strokeOpacity="0.3" />
      <path d="M100 10C75 55 40 110 45 175C48 200 65 235 100 260" fill="#3a8a55" opacity="0.15" />
    </svg>
  );
}

function PalmFrond({ style }: { style?: React.CSSProperties }) {
  return (
    <svg viewBox="0 0 180 300" style={style} fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="palm" x1="0" y1="0" x2="0.5" y2="1">
          <stop offset="0%" stopColor="#3d8b5a" />
          <stop offset="100%" stopColor="#1a5c34" />
        </linearGradient>
      </defs>
      <path d="M90 290C88 200 85 100 90 10" stroke="#4a9e6a" strokeWidth="3" strokeOpacity="0.7" />
      <path d="M88 40C60 55 20 50 5 35" stroke="url(#palm)" strokeWidth="12" strokeLinecap="round" opacity="0.8" />
      <path d="M87 80C55 90 15 80 0 65" stroke="url(#palm)" strokeWidth="11" strokeLinecap="round" opacity="0.75" />
      <path d="M86 120C50 130 10 115 0 100" stroke="url(#palm)" strokeWidth="10" strokeLinecap="round" opacity="0.7" />
      <path d="M85 160C55 170 15 155 5 140" stroke="url(#palm)" strokeWidth="9" strokeLinecap="round" opacity="0.65" />
      <path d="M85 200C60 210 25 195 10 180" stroke="url(#palm)" strokeWidth="8" strokeLinecap="round" opacity="0.6" />
      <path d="M92 40C120 55 160 50 175 35" stroke="url(#palm)" strokeWidth="12" strokeLinecap="round" opacity="0.8" />
      <path d="M93 80C125 90 165 80 180 65" stroke="url(#palm)" strokeWidth="11" strokeLinecap="round" opacity="0.75" />
      <path d="M94 120C130 130 170 115 180 100" stroke="url(#palm)" strokeWidth="10" strokeLinecap="round" opacity="0.7" />
      <path d="M95 160C125 170 165 155 175 140" stroke="url(#palm)" strokeWidth="9" strokeLinecap="round" opacity="0.65" />
      <path d="M95 200C120 210 155 195 170 180" stroke="url(#palm)" strokeWidth="8" strokeLinecap="round" opacity="0.6" />
    </svg>
  );
}

function BroadLeaf({ style }: { style?: React.CSSProperties }) {
  return (
    <svg viewBox="0 0 160 240" style={style} fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="broad" x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0%" stopColor="#35844c" />
          <stop offset="60%" stopColor="#1e5e33" />
          <stop offset="100%" stopColor="#144425" />
        </linearGradient>
      </defs>
      <path d="M80 5C50 40 10 90 15 155C18 190 45 220 80 235C115 220 142 190 145 155C150 90 110 40 80 5Z" fill="url(#broad)" />
      <path d="M80 20V230" stroke="#c9a84c" strokeWidth="2" strokeOpacity="0.5" />
      <path d="M80 50C60 65 40 75 25 85" stroke="#4a9e6a" strokeWidth="1.2" strokeOpacity="0.4" />
      <path d="M80 50C100 65 120 75 135 85" stroke="#4a9e6a" strokeWidth="1.2" strokeOpacity="0.4" />
      <path d="M80 90C58 105 35 115 22 125" stroke="#4a9e6a" strokeWidth="1.2" strokeOpacity="0.35" />
      <path d="M80 90C102 105 125 115 138 125" stroke="#4a9e6a" strokeWidth="1.2" strokeOpacity="0.35" />
      <path d="M80 130C60 145 38 155 25 165" stroke="#4a9e6a" strokeWidth="1" strokeOpacity="0.3" />
      <path d="M80 130C100 145 122 155 135 165" stroke="#4a9e6a" strokeWidth="1" strokeOpacity="0.3" />
      <path d="M80 170C65 180 48 188 35 195" stroke="#4a9e6a" strokeWidth="1" strokeOpacity="0.25" />
      <path d="M80 170C95 180 112 188 125 195" stroke="#4a9e6a" strokeWidth="1" strokeOpacity="0.25" />
      <path d="M80 5C55 45 25 100 30 160C32 185 50 210 80 230" fill="#4a9e6a" opacity="0.12" />
    </svg>
  );
}

type LeafComponent = "monstera" | "palm" | "broad";

function Sparkle({ style }: { style?: React.CSSProperties }) {
  return (
    <svg viewBox="0 0 24 24" style={style} fill="currentColor">
      <path d="M12 0L13.5 9.5L24 12L13.5 14.5L12 24L10.5 14.5L0 12L10.5 9.5Z" />
    </svg>
  );
}

function Diamond({ style }: { style?: React.CSSProperties }) {
  return (
    <svg viewBox="0 0 20 20" style={style} fill="currentColor">
      <path d="M10 0L20 10L10 20L0 10Z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Layer data — direct colors (oklch CSS vars can't be used in hsl())
// ---------------------------------------------------------------------------

const GOLD_50 = "rgba(255, 200, 61, 0.5)";
const GOLD_30 = "rgba(255, 200, 61, 0.3)";
const GOLD_40 = "rgba(255, 200, 61, 0.4)";
const GOLD_60 = "rgba(255, 200, 61, 0.6)";
const WHITE_20 = "rgba(255, 255, 255, 0.2)";
const WHITE_25 = "rgba(255, 255, 255, 0.25)";
const WHITE_30 = "rgba(255, 255, 255, 0.3)";
const WHITE_40 = "rgba(255, 255, 255, 0.4)";

interface LayerItem {
  component: "leaf" | "sparkle" | "diamond";
  x: string;
  y: string;
  size: number;
  rotation: number;
  color: string;
  leafType?: LeafComponent;
}

const bgItems: LayerItem[] = [
  { component: "sparkle", x: "8%", y: "15%", size: 14, rotation: 0, color: GOLD_50 },
  { component: "diamond", x: "85%", y: "20%", size: 10, rotation: 45, color: GOLD_30 },
  { component: "sparkle", x: "92%", y: "75%", size: 12, rotation: 15, color: WHITE_30 },
  { component: "sparkle", x: "15%", y: "80%", size: 16, rotation: -10, color: GOLD_40 },
  { component: "diamond", x: "50%", y: "10%", size: 8, rotation: 0, color: WHITE_20 },
  { component: "sparkle", x: "70%", y: "85%", size: 10, rotation: 30, color: GOLD_30 },
  { component: "diamond", x: "30%", y: "90%", size: 6, rotation: 20, color: WHITE_25 },
];

const midItems: LayerItem[] = [
  { component: "leaf", x: "3%", y: "30%", size: 80, rotation: -25, color: "", leafType: "monstera" },
  { component: "leaf", x: "90%", y: "40%", size: 70, rotation: 20, color: "", leafType: "palm" },
  { component: "sparkle", x: "25%", y: "20%", size: 18, rotation: 0, color: GOLD_60 },
  { component: "sparkle", x: "75%", y: "15%", size: 14, rotation: 45, color: WHITE_40 },
  { component: "diamond", x: "60%", y: "80%", size: 12, rotation: 30, color: GOLD_50 },
];

const fgItems: LayerItem[] = [
  { component: "leaf", x: "-3%", y: "55%", size: 120, rotation: -35, color: "", leafType: "broad" },
  { component: "leaf", x: "92%", y: "60%", size: 110, rotation: 30, color: "", leafType: "monstera" },
  { component: "leaf", x: "5%", y: "85%", size: 90, rotation: -15, color: "", leafType: "palm" },
  { component: "leaf", x: "88%", y: "80%", size: 95, rotation: 15, color: "", leafType: "broad" },
];

function renderItem(item: LayerItem, i: number) {
  const baseStyle: React.CSSProperties = {
    position: "absolute",
    left: item.x,
    top: item.y,
    width: item.size,
    height: item.size,
    color: item.color,
    transform: `rotate(${item.rotation}deg)`,
    pointerEvents: "none",
  };
  switch (item.component) {
    case "leaf": {
      const LeafComp = item.leafType === "palm" ? PalmFrond : item.leafType === "broad" ? BroadLeaf : MonsteraLeaf;
      return <LeafComp key={i} style={baseStyle} />;
    }
    case "sparkle":
      return <Sparkle key={i} style={baseStyle} />;
    case "diamond":
      return <Diamond key={i} style={baseStyle} />;
  }
}

// ---------------------------------------------------------------------------
// Floating particles
// ---------------------------------------------------------------------------

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  opacity: number;
  duration: number;
  delay: number;
  driftX: number;
  driftY: number;
  type: "dot" | "ring" | "glow";
  color: string;
}

function generateParticles(count: number): Particle[] {
  const particles: Particle[] = [];
  const colors = [
    "hsl(42 100% 62%)",
    "hsl(213 60% 60%)",
    "hsl(0 0% 100%)",
    "hsl(145 50% 50%)",
  ];
  for (let i = 0; i < count; i++) {
    particles.push({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 2 + Math.random() * 4,
      opacity: 0.15 + Math.random() * 0.45,
      duration: 8 + Math.random() * 16,
      delay: Math.random() * -20,
      driftX: 15 + Math.random() * 40,
      driftY: 10 + Math.random() * 30,
      type: Math.random() < 0.6 ? "dot" : Math.random() < 0.7 ? "ring" : "glow",
      color: colors[Math.floor(Math.random() * colors.length)],
    });
  }
  return particles;
}

function FloatingParticles({ count = 50 }: { count?: number }) {
  const particles = useMemo(() => generateParticles(count), [count]);

  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            ...(p.type === "dot"
              ? { backgroundColor: p.color }
              : p.type === "ring"
              ? { backgroundColor: "transparent", border: `1px solid ${p.color}` }
              : { backgroundColor: p.color, boxShadow: `0 0 ${p.size * 2}px ${p.size}px ${p.color}` }),
          }}
          animate={{
            x: [0, p.driftX, -p.driftX * 0.5, p.driftX * 0.3, 0],
            y: [0, -p.driftY, p.driftY * 0.6, -p.driftY * 0.4, 0],
            opacity: [p.opacity, p.opacity * 1.5, p.opacity * 0.6, p.opacity * 1.2, p.opacity],
            scale: [1, 1.2, 0.8, 1.1, 1],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            ease: "easeInOut",
            delay: p.delay,
          }}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface ParallaxHeroProps {
  children?: ReactNode;
  /**
   * Reserva a altura do menu fixo ao centralizar o conteúdo.
   *
   * Opt-in porque o herói serve duas telas: a landing tem um `<nav>` `fixed` por
   * cima, e o `/login` não tem nada. Sem a reserva, o slot centraliza na
   * viewport inteira — inclusive nos 56px cobertos pelo menu —, e conteúdo mais
   * alto encosta o topo nele. Reservar no `/login` empurraria o formulário para
   * baixo sem motivo.
   */
  reserveTopNav?: boolean;
}

export function ParallaxHero({ children, reserveTopNav = false }: ParallaxHeroProps) {
  const prefersReducedMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springConfig = { damping: 25, stiffness: 120 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);

  const bgX = useTransform(smoothX, [-1, 1], [8, -8]);
  const bgY = useTransform(smoothY, [-1, 1], [5, -5]);
  const midX = useTransform(smoothX, [-1, 1], [20, -20]);
  const midY = useTransform(smoothY, [-1, 1], [12, -12]);
  const fgX = useTransform(smoothX, [-1, 1], [40, -40]);
  const fgY = useTransform(smoothY, [-1, 1], [25, -25]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (prefersReducedMotion) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      mouseX.set(nx);
      mouseY.set(ny);
    },
    [mouseX, mouseY, prefersReducedMotion],
  );

  const handleMouseLeave = useCallback(() => {
    mouseX.set(0);
    mouseY.set(0);
  }, [mouseX, mouseY]);

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative w-full h-screen overflow-hidden select-none"
      style={{ backgroundColor: "#08254c" }}
    >
      {/* Gradient background */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 40% 50%, hsl(213 60% 20%) 0%, hsl(213 81% 10%) 60%, hsl(213 81% 6%) 100%)",
        }}
      />

      {!prefersReducedMotion && <FloatingParticles count={55} />}

      {/* Animated gradient orbs */}
      <motion.div
        className="absolute w-[600px] h-[600px] rounded-full opacity-20"
        style={{
          x: bgX,
          y: bgY,
          background: "radial-gradient(circle, hsl(42 100% 62% / 0.3), transparent 70%)",
          top: "10%",
          left: "20%",
        }}
      />
      <motion.div
        className="absolute w-[400px] h-[400px] rounded-full opacity-15"
        style={{
          x: midX,
          y: midY,
          background: "radial-gradient(circle, hsl(213 60% 40% / 0.4), transparent 70%)",
          bottom: "10%",
          right: "15%",
        }}
      />

      {/* Background layer */}
      <motion.div className="absolute inset-0" style={{ x: bgX, y: bgY }}>
        {bgItems.map((item, i) => renderItem(item, i))}
      </motion.div>

      {/* Mid-ground layer */}
      <motion.div className="absolute inset-0" style={{ x: midX, y: midY }}>
        {midItems.map((item, i) => renderItem(item, i))}
      </motion.div>

      {/* Center content slot */}
      <div
        data-slot="hero-content"
        className={cn(
          "absolute inset-0 flex items-center justify-center z-10",
          // A reserva encolhe a área de centralização em vez de mudar o
          // alinhamento: o herói centralizado é o design, o que estava errado
          // era centralizar contando com o espaço escondido atrás do menu.
          reserveTopNav && TOP_NAV_OFFSET_CLASS,
        )}
      >
        {children}
      </div>

      {/* Foreground layer */}
      <motion.div
        className="absolute inset-0 z-20 pointer-events-none"
        style={{ x: fgX, y: fgY }}
      >
        {fgItems.map((item, i) => renderItem(item, i))}
      </motion.div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#08254c] to-transparent z-30" />
    </div>
  );
}
